import React, { useState, useEffect } from 'react';
import { ipfsGateway, resolveIpfsUrl } from '../constants/gameConfig';
import { db } from '../firebase';
import { collection, query, onSnapshot, where } from 'firebase/firestore';

// --- Rarity color helper ---
const getOddsColor = (percent) => {
    if (percent >= 20) return '#9e9e9e';      // Common - grey
    if (percent >= 10) return '#4caf50';      // Uncommon - green
    if (percent >= 5)  return '#2196f3';      // Rare - blue
    if (percent >= 2)  return '#9c27b0';      // Epic - purple
    if (percent >= 0.5) return '#ff9800';     // Legendary - orange
    return '#f44336';                          // Mystic - red
};

const getRarityLabel = (percent) => {
    if (percent >= 20) return 'Common';
    if (percent >= 10) return 'Uncommon';
    if (percent >= 5)  return 'Rare';
    if (percent >= 2)  return 'Epic';
    if (percent >= 0.5) return 'Legendary';
    return 'Mystic';
};

// --- Pack Odds Panel Component ---
function PackOddsPanel({ config }) {
    if (!config || !config.outcomes || config.outcomes.length === 0) {
        return (
            <div style={{ padding: '10px', color: '#888', fontSize: '0.85rem', textAlign: 'center' }}>
                No odds data available for this pack.
            </div>
        );
    }

    const totalWeight = config.outcomes.reduce((sum, o) => sum + (parseInt(o.weight) || 0), 0);
    if (totalWeight === 0) return null;

    // Sort outcomes by weight descending (most common first)
    const sortedOutcomes = [...config.outcomes].sort((a, b) => (parseInt(b.weight) || 0) - (parseInt(a.weight) || 0));

    return (
        <div style={{
            marginTop: '12px',
            background: 'rgba(0,0,0,0.6)',
            border: '1px solid rgba(255,152,0,0.25)',
            borderRadius: '8px',
            padding: '12px',
            textAlign: 'left',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '10px',
                paddingBottom: '8px',
                borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}>
                <span style={{ color: '#ff9800', fontSize: '0.85rem', fontWeight: 'bold' }}>
                    🎲 Drop Rates
                </span>
                <span style={{ color: '#aaa', fontSize: '0.75rem' }}>
                    {config.rollCount || 1} item{(config.rollCount || 1) > 1 ? 's' : ''} per open
                </span>
            </div>

            {/* Odds List */}
            {sortedOutcomes.map((outcome, idx) => {
                const weight = parseInt(outcome.weight) || 0;
                const percent = ((weight / totalWeight) * 100);
                const displayPercent = percent < 0.01 ? '<0.01' : percent.toFixed(2);
                const color = getOddsColor(percent);
                const rarityLabel = getRarityLabel(percent);

                return (
                    <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '5px 0',
                        borderBottom: idx < sortedOutcomes.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    }}>
                        {/* Rarity dot */}
                        <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: color,
                            boxShadow: `0 0 6px ${color}`,
                            flexShrink: 0,
                        }} />

                        {/* Item name */}
                        <span style={{
                            flex: 1,
                            color: '#ddd',
                            fontSize: '0.8rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                            {outcome.name || `Template #${outcome.templateId}`}
                        </span>

                        {/* Rarity label */}
                        <span style={{
                            color: color,
                            fontSize: '0.65rem',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            flexShrink: 0,
                        }}>
                            {rarityLabel}
                        </span>

                        {/* Percentage */}
                        <span style={{
                            color: color,
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            minWidth: '55px',
                            textAlign: 'right',
                            flexShrink: 0,
                        }}>
                            {displayPercent}%
                        </span>
                    </div>
                );
            })}

            {/* Footer */}
            <div style={{
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.7rem',
                color: '#666'
            }}>
                <span>Total Weight: {totalWeight}</span>
                <span>{sortedOutcomes.length} possible outcome{sortedOutcomes.length !== 1 ? 's' : ''}</span>
            </div>
        </div>
    );
}

function UnboxPage({ nfts, session, refreshNfts }) {
    const [unboxConfigs, setUnboxConfigs] = useState([]);
    const [loadingPackId, setLoadingPackId] = useState(null);
    const [feedback, setFeedback] = useState('');
    const [showOddsFor, setShowOddsFor] = useState(null); // track which pack template shows odds

    useEffect(() => {
        const q = query(collection(db, 'unbox_configs'), where('isActive', '==', true));
        const unsub = onSnapshot(q, (snapshot) => {
            setUnboxConfigs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsub();
    }, []);

    // Get all matching unbox configs for a pack template ID
    const getConfigsForTemplate = (templateId) => {
        return unboxConfigs.filter(c => String(c.packTemplateId).trim() === String(templateId).trim());
    };

    // Filter NFTs that match any active unbox config
    const availablePacks = nfts.filter(nft => 
        (nft.schema?.schema_name === 'packs' || nft.schema?.schema_name === 'universe') &&
        unboxConfigs.some(config => String(config.packTemplateId).trim() === String(nft.template?.template_id).trim())
    );

    // Find packs in the wallet that DO NOT have an active config
    const unconfiguredPacks = nfts.filter(nft => 
        (nft.schema?.schema_name === 'packs' || nft.schema?.schema_name === 'universe') && 
        !unboxConfigs.some(config => String(config.packTemplateId).trim() === String(nft.template?.template_id).trim())
    );

    // De-duplicate packs by template ID for the "All Pack Odds" section
    const uniqueConfigs = unboxConfigs.filter(c => c.isActive !== false);

    const handleOpenPack = async (packAssetId, configId) => {
        if (!session) {
            setFeedback("Please login first.");
            return;
        }

        const config = unboxConfigs.find(c => c.id === configId);
        if (!config) {
            setFeedback("Configuration for this pack not found.");
            return;
        }

        setLoadingPackId(packAssetId);
        setFeedback("Initiating unboxing...");

        try {
            let memo = 'unpack';
            if (String(config.packTemplateId).trim() === '907189') {
                if (config.unboxName && config.unboxName.toLowerCase().includes('vx')) {
                    memo = 'unpack omni_vx';
                } else {
                    memo = 'unpack omni';
                }
            }

            setFeedback("Please sign the transaction to open your pack...");

            // 2. Sign Transaction
            const action = {
                account: 'atomicassets',
                name: 'transfer',
                authorization: [{
                    actor: session.actor,
                    permission: session.permission,
                }],
                data: {
                    from: session.actor,
                    to: 'whatifunpack',
                    asset_ids: [packAssetId],
                    memo: memo
                }
            };

            const tx = await session.transact({ actions: [action] }, { blocksBehind: 3, expireSeconds: 120 });
            
            setFeedback("Transaction signed! The Oracle is opening your pack. It may take a few seconds for items to arrive in your inventory...");

            setTimeout(() => {
                if (refreshNfts) refreshNfts();
            }, 5000);

        } catch (err) {
            console.error(err);
            setFeedback(`Error: ${err.message || err.toString()}`);
        } finally {
            setLoadingPackId(null);
        }
    };

    const toggleOdds = (templateId) => {
        setShowOddsFor(prev => prev === templateId ? null : templateId);
    };

    return (
        <div style={{
            backgroundColor: 'rgba(0,0,0,0.7)',
            padding: '40px 20px',
            borderRadius: '15px',
            maxWidth: '1000px',
            margin: 'auto',
            border: '1px solid rgba(255,152,0,0.3)',
            boxShadow: '0 0 20px rgba(255,152,0,0.1)'
        }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <h2 style={{ color: '#ff9800', textShadow: '0 0 10px rgba(255,152,0,0.5)', fontSize: '2.5rem', marginBottom: '10px' }}>
                    Unboxing Station
                </h2>
                <p style={{ color: '#aaa', fontSize: '1.1rem' }}>
                    Open your packs to discover new Firemen, Deeds, Gear, and more!
                </p>
                {feedback && (
                    <div style={{ marginTop: '15px', padding: '15px', background: 'rgba(0,0,0,0.8)', border: '1px solid #00ffcc', color: '#00ffcc', borderRadius: '5px' }}>
                        {feedback}
                    </div>
                )}
            </div>

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '30px' }}>
                <h3 style={{ color: '#fff', marginBottom: '20px' }}>Your Unopened Packs ({availablePacks.length})</h3>
                
                {availablePacks.length > 0 ? (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                        gap: '20px'
                    }}>
                        {availablePacks.map(pack => {
                            const templateId = pack.template?.template_id;
                            const matchingConfigs = getConfigsForTemplate(templateId);

                            return (
                                <div key={pack.asset_id} style={{
                                    background: 'rgba(255,255,255,0.05)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: '10px',
                                    padding: '15px',
                                    textAlign: 'center',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                }}>
                                    <img
                                        src={resolveIpfsUrl(pack.template?.immutable_data?.img || pack.data?.img)}
                                        alt={pack.name}
                                        style={{
                                            width: '100%',
                                            height: '200px',
                                            objectFit: 'contain',
                                            borderRadius: '8px',
                                            marginBottom: '15px'
                                        }}
                                    />
                                    <h4 style={{ margin: '0 0 10px 0', color: '#fff', fontSize: '1.1rem' }}>{pack.name}</h4>
                                    <p style={{ margin: '0 0 15px 0', color: '#aaa', fontSize: '0.8rem' }}>
                                        ID: {pack.asset_id} | Tpl: {templateId}
                                    </p>
                                    
                                    {/* Render an option for each matching config */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                        {matchingConfigs.map(config => {
                                            const toggleId = `${pack.asset_id}_${config.id}`;
                                            const isOddsOpen = showOddsFor === toggleId;
                                            
                                            return (
                                                <div key={config.id} style={{ background: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <div style={{ fontSize: '0.9rem', color: '#fff', marginBottom: '8px', fontWeight: 'bold' }}>
                                                        {config.unboxName || "Unbox"}
                                                    </div>
                                                    <button 
                                                        className="modern-button"
                                                        onClick={() => handleOpenPack(pack.asset_id, config.id)}
                                                        disabled={loadingPackId === pack.asset_id}
                                                        style={{ 
                                                            width: '100%', 
                                                            margin: 0,
                                                            background: loadingPackId === pack.asset_id ? '#555' : 'linear-gradient(145deg, #ff9800, #f57c00)'
                                                        }}
                                                    >
                                                        {loadingPackId === pack.asset_id ? 'Opening...' : `Open Pack`}
                                                    </button>

                                                    {/* View Odds Toggle */}
                                                    {config.outcomes && config.outcomes.length > 0 && (
                                                        <>
                                                            <button
                                                                onClick={() => toggleOdds(toggleId)}
                                                                style={{
                                                                    width: '100%',
                                                                    marginTop: '8px',
                                                                    padding: '6px 12px',
                                                                    background: 'transparent',
                                                                    border: `1px solid ${isOddsOpen ? '#ff9800' : 'rgba(255,255,255,0.15)'}`,
                                                                    borderRadius: '6px',
                                                                    color: isOddsOpen ? '#ff9800' : '#888',
                                                                    fontSize: '0.8rem',
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                }}
                                                            >
                                                                {isOddsOpen ? '▲ Hide Odds' : '🎲 View Odds'}
                                                            </button>
                                                            {isOddsOpen && <PackOddsPanel config={config} />}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        background: 'rgba(255,255,255,0.02)',
                        borderRadius: '10px'
                    }}>
                        <p style={{ color: '#aaa', fontSize: '1.1rem', margin: 0 }}>
                            You don't have any configured packs ready to open.
                        </p>
                    </div>
                )}

                {/* --- ALL PACK ODDS SECTION --- */}
                {uniqueConfigs.length > 0 && (
                    <div style={{
                        marginTop: '40px',
                        borderTop: '1px solid rgba(255,152,0,0.2)',
                        paddingTop: '30px',
                    }}>
                        <h3 style={{ color: '#ff9800', marginBottom: '5px' }}>📊 All Pack Odds</h3>
                        <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '20px' }}>
                            Full drop rate breakdown for every active pack type.
                        </p>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            gap: '15px'
                        }}>
                            {uniqueConfigs.map(config => (
                                <div key={config.id} style={{
                                    background: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(255,152,0,0.15)',
                                    borderRadius: '10px',
                                    padding: '15px',
                                }}>
                                    <h4 style={{ margin: '0 0 5px 0', color: '#fff', fontSize: '1rem' }}>
                                        {config.unboxName || `Pack #${config.packTemplateId}`}
                                    </h4>
                                    <p style={{ margin: '0 0 5px 0', color: '#666', fontSize: '0.75rem' }}>
                                        Template ID: {config.packTemplateId}
                                    </p>
                                    <PackOddsPanel config={config} />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {unconfiguredPacks.length > 0 && (
                    <div style={{ marginTop: '40px', borderTop: '1px dashed rgba(255,255,255,0.2)', paddingTop: '20px' }}>
                        <h4 style={{ color: '#ffb74d', marginBottom: '10px' }}>⚠️ Unconfigured Packs ({unconfiguredPacks.length})</h4>
                        <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '20px' }}>These packs are in your wallet, but no active Unboxing Config matches their Template ID. Create a config in the Admin Panel to open them.</p>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                            gap: '15px',
                            opacity: 0.6
                        }}>
                            {unconfiguredPacks.map(pack => (
                                <div key={pack.asset_id} style={{
                                    background: 'rgba(255,0,0,0.05)',
                                    border: '1px solid rgba(255,0,0,0.2)',
                                    borderRadius: '10px',
                                    padding: '10px',
                                    textAlign: 'center',
                                }}>
                                    <h5 style={{ margin: '0 0 5px 0', color: '#fff', fontSize: '0.9rem' }}>{pack.name}</h5>
                                    <p style={{ margin: 0, color: '#aaa', fontSize: '0.7rem' }}>
                                        Tpl: {pack.template?.template_id}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default UnboxPage;
